import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import vi from './locales/vi.json';

/**
 * EAA-1845: from-scratch i18n init for the portal's own page/component copy.
 * Separate from `@ai-content-hub/ui`'s own `useUIText()` catalog (that one
 * covers only the shared kit's built-in component strings and stays pinned to
 * `vi` — see `UiKitProvider.tsx`); this instance is what `src/pages` and
 * `src/components` call through `useTranslation()`.
 *
 * `escapeValue: false` — React already escapes interpolated values,
 * i18next's own HTML-escaping would double-escape them.
 */

/** Sibling of ThemeProvider's `THEME_STORAGE_KEY` — same `portal.console.*`
 *  localStorage convention and the same read-once/write-on-change shape,
 *  just implemented as i18next event wiring instead of React state, since
 *  i18next (not a context) is already the shared source of truth here. */
const LANGUAGE_STORAGE_KEY = 'portal.console.language';

function readStoredLanguage(): 'en' | 'vi' | null {
    try {
        const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
        return stored === 'en' || stored === 'vi' ? stored : null;
    } catch {
        // Private-mode / storage-disabled — fall through to the default below.
        return null;
    }
}

function applyLanguageAttribute(language: string): void {
    document.documentElement.lang = language;
}

void i18n.use(initReactI18next).init({
    resources: {
        en: { translation: en },
        vi: { translation: vi },
    },
    lng: readStoredLanguage() ?? 'vi',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
});

applyLanguageAttribute(i18n.language);

i18n.on('languageChanged', (language) => {
    applyLanguageAttribute(language);
    try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
        // Best-effort persistence only — private mode / quota errors are not fatal.
    }
});

export default i18n;
